import { ChevronDown } from '@drift-labs/icons';
import TradeInput from 'src/components/Inputs/TradeInput';
import { BigNum, isVariant, PositionDirection } from '@drift-labs/sdk';
import { MarketId, UIOrderType } from '@drift/common';
import React, { useCallback } from 'react';
import { cj } from 'src/utils/classNames';
import TpSlOrderFormEstimatedPnL from 'src/components/TradeForm/TpSlOrderForm/TpSlOrderFormEstimatedPnL';
import { useSyncLocalStorage } from 'src/hooks/useSyncLocalStorage';
import { DEFAULT_TP_SL_TRIGGER_PERCENT_BASIS } from './consts';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import useModal from 'src/components/Modals/hooks/useModal';

interface Props {
	isTakeProfit: boolean;
	baseOrderDirection: PositionDirection;
	triggerPrice: BigNum | null;
	triggerPriceStringValue: string;
	triggerPercentStringValue: string;
	triggerOrderDirection: PositionDirection;
	entrySize: BigNum;
	entryPrice: BigNum;
	selectedMarketId: MarketId | undefined;
	updatePrice: (price: string, isTakeProfit: boolean) => void;
	updatePercent: (percent: string, isTakeProfit: boolean) => void;
	updateOrders: () => void;
	orderType: UIOrderType;
	disabled: boolean;
	invalid: boolean;
}

const TpSlOrderFormInputs = React.memo(function TpSlOrderFormInputs({
	isTakeProfit,
	baseOrderDirection,
	triggerPrice,
	triggerPriceStringValue,
	triggerPercentStringValue,
	triggerOrderDirection,
	entrySize,
	entryPrice,
	selectedMarketId,
	updatePrice,
	updatePercent,
	updateOrders,
	orderType,
	disabled,
	invalid,
}: Props) {
	const { show } = useModal();
	const isMobileScreenSize = useIsMobileScreenSize();

	const [triggerPercentBasis] = useSyncLocalStorage(
		'tradeFormTpSlTriggerPercentBasis',
		DEFAULT_TP_SL_TRIGGER_PERCENT_BASIS
	);

	const showTriggerPercentBasisModal = () => {
		if (disabled) {
			return;
		}

		show({ type: 'tp_sl_trigger_percent_basis', props: {} });
	};

	const handleUpdatePrice = useCallback(
		(newValue: string) => {
			updatePrice(newValue, isTakeProfit);
		},
		[updatePrice, isTakeProfit]
	);

	const handleUpdatePercent = useCallback(
		(newValue: string) => {
			updatePercent(newValue, isTakeProfit);
		},
		[updatePercent, isTakeProfit]
	);

	const { percentBasisTriggerLabel, percentInputPlaceholder } = (() => {
		if (isMobileScreenSize) {
			return {
				percentBasisTriggerLabel: '%',
				percentInputPlaceholder:
					triggerPercentBasis === 'price' ? 'Price' : 'ROI',
			};
		}

		return {
			percentBasisTriggerLabel:
				triggerPercentBasis === 'price' ? 'Price %' : 'ROI %',
			percentInputPlaceholder: undefined,
		};
	})();

	const changeTriggerPercentBasisButton = (
		<button
			className={cj(
				'flex items-center gap-x-px typo-b4 whitespace-nowrap',
				disabled ? 'text-text-disabled' : 'text-text-secondary',
				isMobileScreenSize &&
					'border-l border-input-border pl-xs text-text-secondary'
			)}
			onClick={showTriggerPercentBasisModal}
		>
			{percentBasisTriggerLabel}
			<ChevronDown size={14} />
		</button>
	);

	const percentSign = (
		<span
			className={cj(
				'ml-0 text-text-default transition-colors',
				disabled ? 'text-text-disabled' : 'text-text-default'
			)}
		>
			%
		</span>
	);

	const percentInputPrefix = !isMobileScreenSize
		? changeTriggerPercentBasisButton
		: undefined;

	const percentInputSuffix = isMobileScreenSize
		? changeTriggerPercentBasisButton
		: percentSign;

	return (
		<div className="flex flex-col gap-y-1">
			<div className="grid grid-cols-2 gap-x-xs">
				<TradeInput
					id={isTakeProfit ? 'tp-price-input' : 'sl-price-input'}
					size="xs"
					labelVariant="inset"
					label={isTakeProfit ? 'TP' : 'SL'}
					onChange={handleUpdatePrice}
					onBlur={updateOrders}
					value={triggerPriceStringValue}
					disabled={disabled}
				/>
				<TradeInput
					id={isTakeProfit ? 'tp-percent-input' : 'sl-percent-input'}
					size="xs"
					labelVariant="none"
					onChange={handleUpdatePercent}
					onBlur={updateOrders}
					value={triggerPercentStringValue}
					prefix={percentInputPrefix}
					placeholder={percentInputPlaceholder}
					suffix={percentInputSuffix}
					disabled={disabled}
					inputClassName={cj(isMobileScreenSize && 'placeholder:text-center')}
					prefixClassName="pr-px"
					suffixClassName="pl-2xs not-sm:pr-xs"
				/>
			</div>
			<div className="flex items-center gap-x-1 self-end typo-b5 text-text-secondary">
				<span>Est. P&L:</span>
				{!invalid && selectedMarketId && triggerPrice ? (
					<TpSlOrderFormEstimatedPnL
						currentPositionSize={entrySize}
						currentPositionEntryPrice={entryPrice}
						currentPositionDirection={
							isVariant(baseOrderDirection, 'long') ? 'long' : 'short'
						}
						side={isVariant(triggerOrderDirection, 'long') ? 'buy' : 'sell'}
						limitExitSize={entrySize}
						limitExitPrice={triggerPrice}
						isMarketOrder={orderType === 'market'}
					/>
				) : (
					<span>-</span>
				)}
			</div>
		</div>
	);
});

export default TpSlOrderFormInputs;
